import Enumerable from "linq";
import {
  IDrawableItem,
  AbstractPaintableItem,
  BoxLineItem,
  CircleItem,
  TriangleItem,
  CrossItem,
  ILineDrawable,
  LineItem,
  PolylineItem,
} from "../../../../../domain/document/items";
import { Rectangle, Point, Size, LineKinds, ArrowKinds } from "../../../../../domain/drawing";
import { TextureFactory } from "./TextureFactory";

export interface ICanvasDrawerCreatable {
  create(source: IDrawableItem): ICanvasDrawable;
}

export interface ICanvasDrawable {
  getRequiredRectangle(): Rectangle;
  draw(context: CanvasRenderingContext2D): void;
}

export class CanvasDrawerFactory implements ICanvasDrawerCreatable {
  create(source: IDrawableItem): ICanvasDrawable {
    return Enumerable.from(this._factories)
      .select(f => f.create(source))
      .firstOrDefault(d => !!d);
  }

  private readonly _factories: ICanvasDrawerCreatable[] = [
    new BoxLineDrawerFactory(),
    new CircleDrawerFactory(),
    new CrossDrawerFactory(),
    new LineDrawerFactory(),
    new PolylineDrawerFactory(),
    new TriangleDrawerFactory(),
  ];
}

abstract class AbstractPaintableDrawerFactory implements ICanvasDrawerCreatable {
  create(source: IDrawableItem): ICanvasDrawable {
    return this.createDrawer(source, AbstractPaintableDrawerFactory._textureFactory);
  }

  protected abstract createDrawer(source: IDrawableItem, textureFactory: TextureFactory): ICanvasDrawable;

  private static readonly _textureFactory = new TextureFactory();
}

class BoxLineDrawerFactory extends AbstractPaintableDrawerFactory {
  protected createDrawer(source: IDrawableItem, textureFactory: TextureFactory): ICanvasDrawable {
    return (source instanceof BoxLineItem)
      ? new BoxLineDrawer(source, textureFactory)
      : null;
  }
}

class CircleDrawerFactory extends AbstractPaintableDrawerFactory {
  protected createDrawer(source: IDrawableItem, textureFactory: TextureFactory): ICanvasDrawable {
    return (source instanceof CircleItem)
      ? new CircleDrawer(source, textureFactory)
      : null;
  }
}

class CrossDrawerFactory extends AbstractPaintableDrawerFactory {
  protected createDrawer(source: IDrawableItem, textureFactory: TextureFactory): ICanvasDrawable {
    return (source instanceof CrossItem)
      ? new CrossDrawer(source)
      : null;
  }
}

class LineDrawerFactory extends AbstractPaintableDrawerFactory {
  protected createDrawer(source: IDrawableItem, textureFactory: TextureFactory): ICanvasDrawable {
    return (source instanceof LineItem)
      ? new LineDrawer(source)
      : null;
  }
}

class PolylineDrawerFactory extends AbstractPaintableDrawerFactory {
  protected createDrawer(source: IDrawableItem, textureFactory: TextureFactory): ICanvasDrawable {
    return (source instanceof PolylineItem)
      ? new PolylineDrawer(source)
      : null;
  }
}

class TriangleDrawerFactory extends AbstractPaintableDrawerFactory {
  protected createDrawer(source: IDrawableItem, textureFactory: TextureFactory): ICanvasDrawable {
    return (source instanceof TriangleItem)
      ? new TriangleDrawer(source, textureFactory)
      : null;
  }
}

abstract class AbstractLineDrawer<T extends IDrawableItem & ILineDrawable> implements ICanvasDrawable {
  constructor(private readonly _source: T) {}

  getRequiredRectangle(): Rectangle {
    return this.toRequiredRectangle(this._source);
  }

  protected toRequiredRectangle(source: T): Rectangle {
    // 必要なら派生クラスでoverrideする想定.既定動作としては線幅でアジャストする.
    return new Rectangle(this.toOffset(source), source.geometry.rectangle.size.add(new Size(source.lineStyle.width, source.lineStyle.width)));
  }

  private toOffset(source: T): Point {
    const offset = Math.ceil(source.lineStyle.width / 2);
    return new Point(-offset, -offset);
  }

  draw(context: CanvasRenderingContext2D): void {
    context.beginPath();
    context.setLineDash(this._source.lineStyle.kind === LineKinds.Dotted ? [3, 3] : []);
    this.setPath(context, this._source, this._source.geometry.rectangle.topLeft.add(this.getRequiredRectangle().topLeft));
    this.paint(context, this._source, this.getRequiredRectangle().topLeft.multiply(-1));
    context.strokeStyle = this._source.lineStyle.color.value;
    context.lineWidth = this._source.lineStyle.width;
    context.stroke();
  }
  protected abstract setPath(context: CanvasRenderingContext2D, source: T, offset: Point): void;

  protected paint(context: CanvasRenderingContext2D, source: T, offset: Point): void {
    // 必要なら派生クラスでoverrideする想定.
  }

  protected fromAngle(angle: number): number {
    return angle * Math.PI / 180;
  }
}

abstract class AbstractPaintableDrawer<T extends AbstractPaintableItem> extends AbstractLineDrawer<T> {
  constructor(source: T, private readonly _textureFactory: TextureFactory) {
    super(source);
  }

  protected paint(context: CanvasRenderingContext2D, source: T, offset: Point): void {
    const textures = this._textureFactory.create(source.paintStyle, offset);
    for (const texture of textures) {
      context.fillStyle = context.createPattern(texture, "repeat");
      context.fill();
    }
  }
}

class BoxLineDrawer extends AbstractPaintableDrawer<BoxLineItem> {
  constructor(source: BoxLineItem, textureFactory: TextureFactory) {
    super(source, textureFactory);
  }

  protected setPath(context: CanvasRenderingContext2D, source: BoxLineItem, offset: Point): void {
    const rectangle = source.geometry.rectangle;
    const topLeft = rectangle.topLeft.subtract(offset);
    const topRight = rectangle.topRight.subtract(offset);
    const bottomRight = rectangle.bottomRight.subtract(offset);
    const bottomLeft = rectangle.bottomLeft.subtract(offset);
    context.moveTo(topLeft.x, topLeft.y);
    context.lineTo(topRight.x, topRight.y);
    context.lineTo(bottomRight.x, bottomRight.y);
    context.lineTo(bottomLeft.x, bottomLeft.y);
    context.lineTo(topLeft.x, topLeft.y);
    context.lineTo(topRight.x, topRight.y); // 書き出しで終わると線が太い時に角が丸まってしまうのでもう1辺書くことで回避.
  }
}

class CircleDrawer extends AbstractPaintableDrawer<CircleItem> {
  constructor(source: CircleItem, textureFactory: TextureFactory) {
    super(source, textureFactory);
  }

  protected setPath(context: CanvasRenderingContext2D, source: CircleItem, offset: Point): void {
    const center = source.geometry.rectangle.center.subtract(offset);
    const radius = source.geometry.rectangle.size.multiply(0.5);
    // IEはellipse非サポート...代替実装.
    // context.ellipse(radius.width, radius.height, radius.width, radius.height, this.fromAngle(0), this.fromAngle(0), this.fromAngle(360), false);
    this.ellipse(context, center.x, center.y, radius.width, radius.height, this.fromAngle(0), this.fromAngle(360), false);
  }

  private ellipse(context: CanvasRenderingContext2D, x: number, y: number, radiusX: number, radiusY: number, startAngle: number, endAngle: number, anticlockwise?: boolean): void {
    context.save();
    context.scale(1, radiusY / radiusX);
    context.arc(x, y + ((radiusX - radiusY) + ((y - radiusX) * (1 - (y / x)))) * (radiusX / radiusY), radiusX, startAngle, endAngle, anticlockwise);
    context.restore();
  }
}

class CrossDrawer extends AbstractLineDrawer<CrossItem> {
  constructor(source: CrossItem) {
    super(source);
  }

  protected setPath(context: CanvasRenderingContext2D, source: CrossItem, offset: Point): void {
    const rectangle = source.geometry.rectangle;
    const topLeft = rectangle.topLeft.subtract(offset);
    const topRight = rectangle.topRight.subtract(offset);
    const bottomRight = rectangle.bottomRight.subtract(offset);
    const bottomLeft = rectangle.bottomLeft.subtract(offset);
    context.moveTo(topLeft.x, topLeft.y);
    context.lineTo(bottomRight.x, bottomRight.y);
    context.moveTo(topRight.x, topRight.y);
    context.lineTo(bottomLeft.x, bottomLeft.y);
  }
}

class LineDrawer extends AbstractLineDrawer<LineItem> {
  constructor(source: LineItem) {
    super(source);
  }

  protected setPath(context: CanvasRenderingContext2D, source: LineItem, offset: Point): void {
    const from = source.vector.from.subtract(offset);
    const to = source.vector.to.subtract(offset);
    if (source.lineStyle.kind === LineKinds.Double) {
      this.setDoublePath(context, from, to);
      return;
    }
    context.moveTo(from.x, from.y);
    context.lineTo(to.x, to.y);
    switch (source.arrowKind) {
      case ArrowKinds.ArrowFrom:
        this.setArrowPath(context, to, from);
        break;
      case ArrowKinds.ArrowTo:
        this.setArrowPath(context, from, to);
        break;
      case ArrowKinds.ArrowBoth:
        this.setArrowPath(context, to, from);
        this.setArrowPath(context, from, to);
        break;
    }
  }

  private setDoublePath(context: CanvasRenderingContext2D, from: Point, to: Point): void {
    const angle = this.toAngle(from, to);
    const topLineFrom = this.toRotatedPoint(from, new Point(0, -3), angle);
    const topLineTo = this.toRotatedPoint(to, new Point(0, -3), angle);
    const bottomLineFrom = this.toRotatedPoint(from, new Point(0, 3), angle);
    const bottomLineTo = this.toRotatedPoint(to, new Point(0, 3), angle);
    context.moveTo(topLineFrom.x, topLineFrom.y);
    context.lineTo(topLineTo.x, topLineTo.y);
    context.moveTo(bottomLineFrom.x, bottomLineFrom.y);
    context.lineTo(bottomLineTo.x, bottomLineTo.y);
  }

  private setArrowPath(context: CanvasRenderingContext2D, from: Point, to: Point): void {
    const angle = this.toAngle(from, to);
    const arrowL = this.toRotatedPoint(to, new Point(-16, -8), angle);
    const arrowR = this.toRotatedPoint(to, new Point(-16, 8), angle);
    context.moveTo(to.x, to.y);
    context.lineTo(arrowL.x, arrowL.y);
    context.moveTo(to.x, to.y);
    context.lineTo(arrowR.x, arrowR.y);
  }

  private toAngle(from: Point, to: Point): number {
    return Math.atan2(to.y - from.y, to.x - from.x);
  }

  private toRotatedPoint(base: Point, offset: Point, angle: number): Point {
    return new Point(
      base.x + (offset.x * Math.cos(angle) - offset.y * Math.sin(angle)),
      base.y + (offset.x * Math.sin(angle) + offset.y * Math.cos(angle))); 
  }
}

class PolylineDrawer extends AbstractLineDrawer<PolylineItem> {
  constructor(source: PolylineItem) {
    super(source);
  }

  protected setPath(context: CanvasRenderingContext2D, source: PolylineItem, offset: Point): void {
    for (let i = 0; i < source.points.length; i++) {
      const point = source.points[i].subtract(offset);
      if (i === 0) {
        context.moveTo(point.x, point.y);
      } else {
        context.lineTo(point.x, point.y);
      }
    }
  }
}

class TriangleDrawer extends AbstractPaintableDrawer<TriangleItem> {
  constructor(source: TriangleItem, textureFactory: TextureFactory) {
    super(source, textureFactory);
  }

  protected setPath(context: CanvasRenderingContext2D, source: TriangleItem, offset: Point): void {
    const rectangle = source.geometry.rectangle;
    const top = source.geometry.rectangle.topRight.subtract(offset).multiply(0.5);
    const bottomRight = rectangle.bottomRight.subtract(offset);
    const bottomLeft = rectangle.bottomLeft.subtract(offset);
    context.moveTo(top.x, top.y);
    context.lineTo(bottomRight.x, bottomRight.y);
    context.lineTo(bottomLeft.x, bottomLeft.y);
    context.lineTo(top.x, top.y);
  }
}
